import * as THREE from 'three';
import { collectEntityTargetExtentPoints, collectFrameExtentPoints, resolveSectorRingFrame, resolveSectorRingItemFrame } from '../../rendering/_pure/frameBounds.js';
import { enclosePointerPoints, sampleGuidePointer } from '../../rendering/_pure/emphasisPointer.js';
import { resolveGuideGeometry, resolveItemContour, resolveItemFlag } from '../../rendering/_pure/itemContour.js';
export function resolveGuidanceItems(frame, targets, whilePresent = false) {
    if (!targets.length)
        throw new Error('Visual guidance requires at least one target.');
    const keys = new Set();
    for (const target of targets) {
        if (target.targetKind === 'item') {
            const item = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'entity'
                ? collection.items : []).find(item => item.itemKey === target.itemKey);
            if (!item) {
                if (whilePresent)
                    continue;
                throw new Error(`Visual guidance item '${target.itemKey}' is not present.`);
            }
            keys.add(item.itemKey);
        }
        else {
            const constellation = frame.constellations.find(entry => entry.constellationKind === 'entity'
                && entry.constellationKey === target.constellationKey);
            if (!constellation) {
                if (whilePresent)
                    continue;
                throw new Error(`Visual guidance constellation '${target.constellationKey}' is not present.`);
            }
            const collection = frame.itemCollections.find(entry => entry.itemCollectionKind === 'entity'
                && entry.itemCollectionKey === constellation.itemCollectionKey);
            if (!collection || collection.itemCollectionKind !== 'entity' || !collection.items.length) {
                if (whilePresent)
                    continue;
                throw new Error(`Visual guidance constellation '${target.constellationKey}' has no items.`);
            }
            for (const item of collection.items)
                keys.add(item.itemKey);
        }
    }
    return [...keys];
}
function validateEmphasis(emphasis) {
    if (!emphasis.marks.length)
        throw new Error('Visual emphasis requires at least one mark.');
    if (new Set(emphasis.marks).size !== emphasis.marks.length)
        throw new Error('Visual emphasis repeats a mark.');
    if (emphasis.caption !== undefined && !emphasis.marks.includes('pointer'))
        throw new Error('Visual emphasis caption requires the pointer mark.');
}
function emphasesWith(guidance, mark) {
    const emphases = guidance?.emphases ?? [];
    for (const emphasis of emphases)
        validateEmphasis(emphasis);
    return emphases.filter(emphasis => emphasis.marks.includes(mark));
}
export function withGuidanceEmphasis(frame, guidance) {
    const bordered = emphasesWith(guidance, 'border');
    if (!bordered.length)
        return frame;
    const states = { ...frame.itemParametricState?.entityItemByKey };
    const sectorItems = new Set(frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'entity' && frame.constellations.some(entry => entry.constellationKind === 'entity' && entry.itemCollectionKey === collection.itemCollectionKey
        && frame.constellationParametricState?.entityConstellationByKey?.[entry.constellationKey]?.layout?.layoutKind === 'sector-ring')
        ? collection.items : [])
        .map(item => item.itemKey));
    const emphasized = new Set();
    for (const emphasis of bordered) {
        for (const key of resolveGuidanceItems(frame, emphasis.targets, emphasis.lifetime === 'while-present')) {
            if (emphasized.has(key))
                throw new Error(`Conflicting visual emphasis for '${key}'.`);
            emphasized.add(key);
            const state = states[key];
            if (sectorItems.has(key)) {
                states[key] = { ...state, shapePresentation: { ...state?.shapePresentation,
                        border: { ...state?.shapePresentation?.border, color: emphasis.color, opacity: 1, width: 2 } } };
            }
            else if (state?.shape?.shapeKind === 'panel' || state?.shape?.shapeKind === 'sector-slice'
                || state?.shape?.shapeKind === 'extruded-profile' || state?.shape?.shapeKind === 'sphere') {
                states[key] = { ...state, shape: { ...state.shape,
                        border: { ...state.shape.border, color: emphasis.color, opacity: 1, width: 2 } } };
            }
            else {
                throw new Error(`Border emphasis requires an outlinable item; '${key}' is neither a ring cell, a panel, a sector slice, an extruded profile nor a sphere.`);
            }
        }
    }
    return { ...frame, itemParametricState: { ...frame.itemParametricState, entityItemByKey: states } };
}
/** One spotlight bounds every spotlit item; emphasis without placed geometry casts no light. */
export function resolveEmphasisSpotlight(frame, guidance) {
    const emphases = emphasesWith(guidance, 'spotlight');
    if (!emphases.length)
        return undefined;
    if (new Set(emphases.map(emphasis => emphasis.color)).size > 1)
        throw new Error('Spotlight emphases share one color.');
    const keys = new Set(emphases.flatMap(emphasis => resolveGuidanceItems(frame, emphasis.targets, emphasis.lifetime === 'while-present')));
    if (!keys.size)
        return undefined;
    const points = collectEntityTargetExtentPoints(frame, keys);
    if (!points.length)
        return undefined;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const point of points)
        for (let axis = 0; axis < 3; axis++) {
            min[axis] = Math.min(min[axis], point[axis]);
            max[axis] = Math.max(max[axis], point[axis]);
        }
    const center = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const radius = Math.max(...points.map(point => Math.hypot(point[0] - center[0], point[1] - center[1], point[2] - center[2])));
    return { center, radius, color: emphases[0].color };
}
const targetKey = (target) => target.targetKind === 'item' ? target.itemKey : target.constellationKey;
const roundedKey = (values) => values.map(value => Math.round(value * 1e6) / 1e6).join(',');
/**
 * One contour per group of an emphasis's ring constellations that share a center and orientation, spanning their
 * innermost to outermost radius. Item targets select angular slices; absent topology draws nothing.
 */
export function resolveEmphasisContours(frame, guidance) {
    const authored = resolveGuidanceContours(frame, guidance);
    if (guidance?.guideEmphasis) {
        const guide = resolveGuideGeometry(frame, guidance.guideEmphasis.constellationKey);
        authored.push({ key: guide.key, color: guidance.guideEmphasis.color, position: [0, 0, 0], rotation: [0, 0, 0], points: guide.points });
    }
    const transient = (frame.spaceRenderOptions?.contourEmphases ?? []).flatMap(emphasis => resolveGuidanceContours(frame, {
        key: 'transition-contours',
        emphases: [{ ...emphasis, marks: ['contour'] }],
    }));
    return [...new Map([...authored, ...transient].map(contour => [contour.key, contour])).values()];
}
function resolveGuidanceContours(frame, guidance) {
    const contoured = new Set();
    const shapes = [];
    const rings = emphasesWith(guidance, 'contour').flatMap(emphasis => {
        const groups = new Map();
        for (const target of emphasis.targets) {
            const key = targetKey(target);
            const items = resolveGuidanceItems(frame, [target], emphasis.lifetime === 'while-present');
            if (!items.length && emphasis.lifetime === 'while-present')
                continue;
            for (const item of items) {
                if (contoured.has(item))
                    throw new Error(`Conflicting contour emphasis for '${key}'.`);
                contoured.add(item);
            }
            const constellation = frame.constellations.find(c => c.constellationKind === 'entity' && (target.targetKind === 'constellation'
                ? c.constellationKey === target.constellationKey
                : frame.itemCollections.some(collection => collection.itemCollectionKey === c.itemCollectionKey
                    && collection.items.some(item => item.itemKey === target.itemKey))));
            const layout = constellation?.constellationKind === 'entity'
                ? frame.constellationParametricState?.entityConstellationByKey?.[constellation.constellationKey]?.layout : undefined;
            if (layout?.layoutKind !== 'sector-ring') {
                for (const item of items) {
                    const contour = resolveItemContour(frame, item, emphasis.color);
                    if (contour)
                        shapes.push(contour);
                }
                continue;
            }
            const ring = target.targetKind === 'item'
                ? resolveSectorRingItemFrame(frame, items[0])
                : resolveSectorRingFrame(frame, target.constellationKey);
            if (!ring)
                continue;
            const groupKey = `${roundedKey([...ring.position, ...ring.rotation])}:${ring.startAngle !== undefined
                ? roundedKey([ring.startAngle, ring.endAngle]) : 'full'}`;
            const group = groups.get(groupKey);
            if (!group)
                groups.set(groupKey, { keys: [key], ring });
            else {
                group.keys.push(key);
                const inner = ring.innerRadius < group.ring.innerRadius ? ring : group.ring;
                const outer = ring.outerRadius > group.ring.outerRadius ? ring : group.ring;
                group.ring = { ...group.ring, innerRadius: inner.innerRadius, innerHalfHeight: inner.innerHalfHeight,
                    outerRadius: outer.outerRadius, outerHalfHeight: outer.outerHalfHeight };
            }
        }
        return [...groups.values()].map(({ keys, ring }) => ({ key: keys.sort().join('|'), color: emphasis.color, ...ring }));
    });
    const lines = (guidance?.lineContours ?? []).map(line => ({
        key: line.key, color: line.color, position: [0, 0, 0], rotation: [0, 0, 0],
        points: [resolveGuidanceCenter(frame, [line.from]), resolveGuidanceCenter(frame, [line.to])],
    }));
    const flags = (guidance?.flagContours ?? []).map(target => {
        const [key] = resolveGuidanceItems(frame, [{ targetKind: 'item', itemKey: target.itemKey }]);
        const flag = key && resolveItemFlag(frame, key);
        if (!flag)
            throw new Error(`Teaching target '${target.itemKey}' has no flag.`);
        return { key: target.key, color: target.color, position: flag.attachment, rotation: [0, 0, 0],
            flagPostOffset: flag.postOffset, points: [[0, -flag.height / 2, 0], [flag.width, -flag.height / 2, 0],
                [flag.width, flag.height / 2, 0], [0, flag.height / 2, 0], [0, -flag.height / 2, 0]] };
    });
    return [...rings, ...shapes, ...lines, ...flags, ...(guidance?.pathContours ?? []).map(path => ({
            ...path, position: [0, 0, 0], rotation: [0, 0, 0],
        }))];
}
export function resolveTrackingContours(frame, guidance) {
    const tracking = guidance?.trackingContour;
    if (!tracking)
        return [];
    return resolveGuidanceItems(frame, tracking.targets).map(item => {
        const contour = resolveItemContour(frame, item, tracking.color);
        if (!contour)
            throw new Error(`Tracking contour '${item}' has no placed geometry.`);
        return contour;
    });
}
/** One arrow rig per guidance with one arm per emphasis; arms without placed geometry are omitted. */
export function resolveEmphasisPointer(frame, guidance) {
    const emphases = emphasesWith(guidance, 'pointer');
    if (!emphases.length && !guidance?.pointerSweep && !guidance?.guideEmphasis)
        return undefined;
    const placement = guidance.pointerPlacement;
    if (!placement)
        throw new Error('Pointer emphasis requires a placement.');
    if (!(placement.depth >= 0 && placement.depth < 1) || !(placement.lift >= 0))
        throw new Error('Invalid pointer placement.');
    if (guidance?.guideEmphasis) {
        if (emphases.length || guidance.pointerSweep)
            throw new Error('Guide pointer cannot share item emphasis or a sweep.');
        const duration = guidance.guideEmphasis.slideDurationMs;
        if (duration !== undefined && (!Number.isFinite(duration) || duration <= 0))
            throw new Error('Invalid guide slide duration.');
        const guide = { ...resolveGuideGeometry(frame, guidance.guideEmphasis.constellationKey), key: guidance.key,
            ...(duration === undefined ? {} : { slideDurationMs: duration }) };
        const sample = sampleGuidePointer(guide, frame.spaceRenderOptions?.camera?.pose?.position ?? [0, 0, 0]);
        return { color: guidance.guideEmphasis.color,
            ...(guidance.guideEmphasis.caption === undefined ? {} : { caption: guidance.guideEmphasis.caption }),
            placement, guide, arms: [sample.arm], focus: sample.focus };
    }
    if (guidance?.pointerSweep) {
        if (emphases.length)
            throw new Error('A pointer sweep cannot share the pointer with static emphases.');
        const sweep = guidance.pointerSweep;
        if (!Number.isFinite(sweep.durationMs) || sweep.durationMs <= 0)
            throw new Error('Invalid pointer sweep duration.');
        const sphere = (target) => {
            const keys = new Set(resolveGuidanceItems(frame, [target]));
            const result = enclosePointerPoints(collectEntityTargetExtentPoints(frame, keys));
            if (!result)
                throw new Error('Pointer sweep target has no placed geometry.');
            return result;
        };
        const from = sphere(sweep.from), to = sphere(sweep.to);
        const focus = enclosePointerPoints(placement.focus === 'stage' ? collectFrameExtentPoints(frame)
            : collectEntityTargetExtentPoints(frame, new Set(resolveGuidanceItems(frame, [sweep.from, sweep.to]))));
        if (!focus)
            throw new Error('Pointer sweep requires placed focus geometry.');
        return { color: sweep.color, placement, focus,
            arms: [{ key: guidance.key, ...from }],
            sweep: { key: guidance.key, durationMs: sweep.durationMs, from, to, avoid: sweep.avoid.map(sphere) } };
    }
    if (new Set(emphases.map(emphasis => emphasis.color)).size > 1)
        throw new Error('Pointer emphases share one color.');
    const captions = emphases.flatMap(emphasis => emphasis.caption === undefined ? [] : [emphasis.caption]);
    if (captions.length > 1)
        throw new Error('A pointer carries at most one caption.');
    const pointed = emphases.flatMap(emphasis => {
        if (emphasis.pointerGapPixels !== undefined
            && (!Number.isFinite(emphasis.pointerGapPixels) || emphasis.pointerGapPixels < 0)) {
            throw new Error('Pointer gap must be a finite nonnegative pixel distance.');
        }
        const keys = new Set(resolveGuidanceItems(frame, emphasis.targets, emphasis.lifetime === 'while-present'));
        const points = keys.size ? collectEntityTargetExtentPoints(frame, keys) : [];
        const sphere = enclosePointerPoints(points);
        const key = keys.size === 1 ? [...keys][0] : undefined;
        const flag = key && emphasis.pointerAnchor === 'flag-or-item' ? resolveItemFlag(frame, key) : undefined;
        const constellation = key && frame.constellations.find(c => c.constellationKind === 'entity'
            && frame.itemCollections.some(collection => collection.itemCollectionKey === c.itemCollectionKey
                && collection.items.some(item => item.itemKey === key)));
        const sector = key && constellation && constellation.constellationKind === 'entity'
            && frame.constellationParametricState?.entityConstellationByKey?.[constellation.constellationKey]?.layout?.layoutKind === 'sector-ring'
            ? resolveSectorRingItemFrame(frame, key) : undefined;
        const contour = key && emphasis.pointerAnchor === 'flag-or-item' && !flag && !sector ? resolveItemContour(frame, key, emphasis.color) : undefined;
        const ringPoints = sector ? [sector.innerRadius, sector.outerRadius].flatMap(radius => Array.from({ length: 33 }, (_, index) => {
            const angle = (sector.startAngle ?? 0) + index / 32 * ((sector.endAngle ?? Math.PI * 2) - (sector.startAngle ?? 0));
            return [Math.sin(angle) * radius, 0, Math.cos(angle) * radius];
        })) : undefined;
        const shapePoints = contour && 'points' in contour ? contour.points : ringPoints;
        const pose = sector ?? contour;
        const outline = shapePoints && pose ? shapePoints.map(point => {
            const vector = new THREE.Vector3(...point).applyEuler(new THREE.Euler(...pose.rotation))
                .add(new THREE.Vector3(...pose.position));
            return vector.toArray();
        }) : undefined;
        const surface = contour && 'sphereRadius' in contour ? { center: contour.position, radius: contour.sphereRadius } : sphere;
        return surface ? [{ arm: { key: emphasis.targets.map(targetKey).sort().join('|'), ...surface,
                    ...(flag ? { flag } : {}), ...(outline ? { outline } : {}),
                    ...(emphasis.pointerGapPixels === undefined ? {} : { gapPixels: emphasis.pointerGapPixels }) }, points }] : [];
    });
    const arms = pointed.map(entry => entry.arm);
    if (new Set(arms.map(arm => arm.key)).size !== arms.length)
        throw new Error('Conflicting pointer emphasis.');
    if (!arms.length)
        return undefined;
    const focus = enclosePointerPoints(placement.focus === 'stage' ? collectFrameExtentPoints(frame)
        : pointed.flatMap(entry => entry.points));
    if (!focus)
        return undefined;
    return { color: emphases[0].color, placement, focus, arms, ...(captions[0] === undefined ? {} : { caption: captions[0] }) };
}
export function resolveGuidanceCenter(frame, targets) {
    const keys = new Set(resolveGuidanceItems(frame, targets));
    const points = collectEntityTargetExtentPoints(frame, keys);
    if (!points.length)
        throw new Error('Visual guidance target has no placed geometry.');
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const point of points)
        for (let axis = 0; axis < 3; axis++) {
            min[axis] = Math.min(min[axis], point[axis]);
            max[axis] = Math.max(max[axis], point[axis]);
        }
    return [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
}
export function orbitGuidancePose(initial, center, angleRadians, progress) {
    const angle = angleRadians * Math.sin(Math.PI * Math.max(0, Math.min(1, progress)));
    const rotate = (point) => {
        const x = point[0] - center[0], z = point[2] - center[2];
        return [center[0] + x * Math.cos(angle) + z * Math.sin(angle), point[1],
            center[2] - x * Math.sin(angle) + z * Math.cos(angle)];
    };
    return { ...initial, position: rotate(initial.position), target: rotate(initial.target) };
}
